"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const bignumber_1 = require("@ethersproject/bignumber");
const ethers_1 = require("ethers");
const base_1 = require("../base");
const constant_1 = require("../constant");
const util_1 = require("../util");
class BorrowModule extends base_1.TectonicSDKModule {
    constructor() {
        super(...arguments);
        this.huddleTTokenAmount = (tTokenAmount) => tTokenAmount.mul(10001).div(10000);
        this.swapAndRepay = async (tectonicSocketAddress, account, repayAmount, slippageTolerance, path, tTokenAddressOfCollateral, repayAmountSwap) => {
            const deferLiquidityCheckAdapterInstance = this.getSdkBase().getDeferLiquidityCheckAdapter();
            const deferLiquidityCheckAdapterAddress = deferLiquidityCheckAdapterInstance.address;
            const [tTokenAmount, amountInMax] = await this.getTTokenAmountIn(tectonicSocketAddress, repayAmount, slippageTolerance, path);
            const swapTTokenAmount = this.huddleTTokenAmount(tTokenAmount);
            await this.approve(account, swapTTokenAmount, tTokenAddressOfCollateral, deferLiquidityCheckAdapterAddress);
            return (0, util_1.wrapSdkTransaction)(deferLiquidityCheckAdapterInstance.swapAndRepay(tectonicSocketAddress, account, swapTTokenAmount, repayAmountSwap !== null && repayAmountSwap !== void 0 ? repayAmountSwap : repayAmount, amountInMax, path));
        };
        /**
         * @param tectonicSocketAddress
         * @param account
         * @param fromAmount
         * @param slippageTolerance
         * @param path
         * @returns [tTokenAmount, amountInMax]
         *
         */
        this.getTTokenAmountIn = async (tectonicSocketAddress, repayAmount, slippageTolerance, path) => {
            const deferLiquidityCheckAdapter = this.getSdkBase().getDeferLiquidityCheckAdapter();
            return deferLiquidityCheckAdapter.getTTokenAmountIn(tectonicSocketAddress, repayAmount, slippageTolerance, path);
        };
        this.repaySomeWithCollateral = async (tectonicSocketAddress, account, repayAmount, slippageTolerance, path, tTokenAddressOfCollateral) => {
            return this.swapAndRepay(tectonicSocketAddress, account, repayAmount, slippageTolerance, path, tTokenAddressOfCollateral);
        };
        this.repayAllWithCollateral = async (tectonicSocketAddress, account, repayAmount, slippageTolerance, path, tTokenAddressOfCollateral) => {
            return this.swapAndRepay(tectonicSocketAddress, account, repayAmount, slippageTolerance, path, tTokenAddressOfCollateral, ethers_1.ethers.constants.MaxUint256);
        };
        this.approve = async (account, amount, tTokenAddress, spenderAddress) => {
            const sdk = this.getSdkBase();
            const tToken = sdk.getContractByAddress(tTokenAddress);
            const allowance = await tToken.allowance(account, spenderAddress);
            if (allowance.lt(amount)) {
                const tx = await tToken.approve(spenderAddress, ethers_1.ethers.constants.MaxUint256);
                await tx.wait();
            }
        };
        this.swapAndDeposit = async (tectonicSocketAddress, account, fromAmount, slippageTolerance, path, swapTTokenAddress) => {
            const sdk = this.getSdkBase();
            const deferLiquidityCheckAdapterInstance = sdk.getDeferLiquidityCheckAdapter();
            const deferLiquidityCheckAdapterAddress = deferLiquidityCheckAdapterInstance.address;
            const [tTokenAmount, amountOutMin] = await sdk.getTTokenAmountOut(tectonicSocketAddress, fromAmount, slippageTolerance, path);
            await this.approve(account, tTokenAmount, swapTTokenAddress, deferLiquidityCheckAdapterAddress);
            return (0, util_1.wrapSdkTransaction)(deferLiquidityCheckAdapterInstance.swapAndDeposit(tectonicSocketAddress, account, tTokenAmount, amountOutMin, path));
        };
    }
    async borrow(tTokenAddress, amount) {
        if (tTokenAddress !== this.getContractByName('TEther').address) {
            const tErc20 = this.getContractByAddress(tTokenAddress);
            return (0, util_1.wrapSdkTransaction)(tErc20.borrow(bignumber_1.BigNumber.from(amount)));
        }
        else {
            const tEther = this.getContractByAddress(tTokenAddress);
            return (0, util_1.wrapSdkTransaction)(tEther.borrow(bignumber_1.BigNumber.from(amount)));
        }
    }
    async borrowedAmountOfUser(tTokenAddress, borrowerAddress) {
        const tToken = this.getContractByAddress(tTokenAddress);
        return await tToken.callStatic.borrowBalanceCurrent(borrowerAddress);
    }
    async tTokenBorrowAmountOfUser(walletAddress, tTokenAddress) {
        const ITToken = this.getContractByAddress(tTokenAddress);
        return await ITToken.borrowBalanceStored(walletAddress);
    }
    async totalBorrowedAmountInMarket(tTokenAddress) {
        const tToken = this.getContractByAddress(tTokenAddress);
        return await tToken.callStatic.totalBorrowsCurrent();
    }
    async borrowCap(tTokenAddress) {
        //When it's 0, means No Limit.
        //return native amount of underlying token, need to divide erc20 decimals then multiplies USD price to show on UI.
        const tCore = this.getTectonicCore();
        return await tCore.borrowCaps(tTokenAddress);
    }
    async liquidationPenalty() {
        //returns percentage number, e.g. 20.05% will be returned as 20.05
        const tCore = this.getTectonicCore();
        const liquidationIncentiveMantissa = await tCore.liquidationIncentiveMantissa();
        return (Math.round(liquidationIncentiveMantissa
            .mul(100000) // 5 digits precision before rounding. otherwise the 5h digit will be truncated instead of rounded.
            .div(bignumber_1.BigNumber.from(constant_1.TectonicConst.ETH_MANTISSA))
            .toNumber() / 10) / // rounded to 4 digits integer.
            100 - //become a percentage number
            100);
    }
    async borrowRatePerBlock(tTokenAddress) {
        const tToken = this.getContractByAddress(tTokenAddress);
        return await tToken.borrowRatePerBlock();
    }
    async collateralFactor(tTokenAddress) {
        //returns percentage number, e.g. 20.05% will be returned as 20.05
        const tCore = this.getTectonicCore();
        const market = await tCore.markets(tTokenAddress);
        const collateralFactorMantissa = market.collateralFactorMantissa;
        return (Math.round(collateralFactorMantissa
            .mul(100000) // 5 digits precision before rounding. otherwise the 5h digit will be truncated instead of rounded.
            .div(bignumber_1.BigNumber.from(constant_1.TectonicConst.ETH_MANTISSA))
            .toNumber() / 10) / // rounded to 4 digits integer.
            100); //become a percentage number
    }
    async borrowApy(tTokenAddress) {
        const tToken = this.getContractByAddress(tTokenAddress);
        // The current supply rate as an unsigned integer, scaled by 1e18.
        const borrowRate = await tToken.borrowRatePerBlock();
        const dailyYieldRate = 1 +
            Number(ethers_1.ethers.utils.formatUnits(borrowRate.mul(bignumber_1.BigNumber.from(constant_1.TectonicConst.BLOCKS_PER_DAY))));
        const annualYieldRate = (Math.pow(dailyYieldRate, constant_1.TectonicConst.DAYS_PER_YEAR) - 1) * 10000;
        //updated to percentage number with 4 digits precision. same as other functions returning percentage number.
        //e.g. 20.05% will be returned as 20.05
        return Math.round(annualYieldRate) / 100;
    }
    async repayAll(tTokenAddress, borrowerAddress) {
        if (tTokenAddress !== this.getContractByName('TEther').address) {
            const tErc20 = this.getContractByAddress(tTokenAddress);
            const minusOne = bignumber_1.BigNumber.from(2).pow(256).sub(1);
            return (0, util_1.wrapSdkTransaction)(tErc20.repayBorrow(minusOne));
        }
        else {
            const tEther = this.getContractByAddress(tTokenAddress);
            const borrowBalance = await tEther.callStatic.borrowBalanceCurrent(borrowerAddress);
            const maximillion = this.getContractByName('Maximillion');
            return (0, util_1.wrapSdkTransaction)(maximillion.repayBehalf(borrowerAddress, {
                value: bignumber_1.BigNumber.from(borrowBalance).mul(1001).div(1000),
            }));
        }
    }
    async repaySome(tTokenAddress, amountToRepay) {
        if (tTokenAddress !== this.getContractByName('TEther').address) {
            const tErc20 = this.getContractByAddress(tTokenAddress);
            return (0, util_1.wrapSdkTransaction)(tErc20.repayBorrow(amountToRepay));
        }
        else {
            const tEther = this.getContractByAddress(tTokenAddress);
            return (0, util_1.wrapSdkTransaction)(tEther.repayBorrow({ value: bignumber_1.BigNumber.from(amountToRepay) }));
        }
    }
    getSdkBase() {
        return this;
    }
    async repayBehalf(tTokenAddress, behalfAddress, amountToRepay) {
        if (tTokenAddress !== this.getContractByName('TEther').address) {
            const tErc20 = this.getContractByAddress(tTokenAddress);
            return (0, util_1.wrapSdkTransaction)(tErc20.repayBorrowBehalf(behalfAddress, amountToRepay));
        }
        else {
            const tEther = this.getContractByAddress(tTokenAddress);
            return (0, util_1.wrapSdkTransaction)(tEther.repayBorrowBehalf(behalfAddress, {
                value: bignumber_1.BigNumber.from(amountToRepay),
            }));
        }
    }
}
exports.default = BorrowModule;
//# sourceMappingURL=borrow.js.map